import { useEffect } from "react";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { X, MapPin, Scale, AlertTriangle } from "lucide-react";
import { fetcher } from "@/lib/api";

export default function ContenedoresDrawer({ colPoint, open, onClose }) {
  const { t } = useTranslation();
  const cpId = colPoint?.col_point_id;
  const { data } = useSWR(open && cpId ? `/sigeus/col-points/${cpId}` : null, fetcher);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const cp = data?.col_point || colPoint;
  const weighins = data?.recent_weighins || [];
  const alarms = data?.open_alarms || [];

  return (
    <div
      className="fixed inset-0 z-[500] flex justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="contedores-drawer"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="relative w-full max-w-[520px] h-full bg-salnes-ivory overflow-y-auto shadow-2xl">
        <div className="bg-salnes-atlantic text-white p-6 flex items-start gap-4">
          <MapPin size={22} className="mt-1 opacity-90" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">
              {t("fase9.contedores.popup_col_point")} #{cp?.col_point_id}
            </div>
            <h2 className="font-serif text-xl leading-tight truncate">
              {cp?.reference || cp?.external_code || `#${cp?.col_point_id}`}
            </h2>
            <div className="text-xs opacity-80 mt-1">{cp?.address || "—"}</div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="drawer-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <section>
            <h3 className="font-serif text-base text-salnes-ink mb-2 flex items-center gap-2">
              <Scale size={16} strokeWidth={1.6} className="text-salnes-emerald" />
              {t("fase9.contedores.drawer_recent_weighins")}
            </h3>
            {weighins.length ? (
              <div className="rounded-xl border hairline bg-white overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-salnes-sand/40 text-salnes-stone uppercase tracking-[0.12em] text-[10px]">
                    <tr>
                      <th className="px-3 py-2 text-left">{t("fase9.contedores.th_date")}</th>
                      <th className="px-3 py-2 text-left">{t("fase9.contedores.th_waste")}</th>
                      <th className="px-3 py-2 text-right">{t("fase9.contedores.th_kg")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weighins.map((w, i) => (
                      <tr key={i} className="border-t hairline">
                        <td className="px-3 py-2">{String(w.weighing_date || "").slice(0, 10)}</td>
                        <td className="px-3 py-2 text-salnes-ink">{w.waste_type || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono">{w.weight_kg?.toLocaleString?.() || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-salnes-stone italic">{t("fase9.contedores.drawer_no_weighins")}</p>
            )}
          </section>

          <section>
            <h3 className="font-serif text-base text-salnes-ink mb-2 flex items-center gap-2">
              <AlertTriangle size={16} strokeWidth={1.6} className="text-salnes-coral" />
              {t("fase9.contedores.drawer_open_alarms")}
            </h3>
            {alarms.length ? (
              <ul className="space-y-2">
                {alarms.map((a) => (
                  <li key={a.alarm_event_id} className="rounded-xl border hairline bg-white px-3 py-2 text-xs">
                    <div className="font-medium text-salnes-ink">{a.type_name || `Alarm #${a.type_id}`}</div>
                    <div className="text-salnes-stone">{String(a.opened_at || "").slice(0, 19)}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-salnes-stone italic">{t("fase9.contedores.drawer_no_alarms")}</p>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
